"use client";

import { useState } from "react";
import { toast } from "sonner";
import { CopyButton } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  buildTitlePage,
  buildTitlePageFragment,
  buildTitlePagePlainText,
  buildTitlePageWordHtml,
  type PreviewBlock,
  type TitlePageInput,
  type TitlePageStyle,
} from "@/lib/title-page";

type FieldKey = Exclude<keyof TitlePageInput, never>;

interface FieldConfig {
  key: FieldKey;
  multiline?: boolean;
}

const FIELDS: Record<FieldKey, FieldConfig> = {
  title: { key: "title" },
  authors: { key: "authors", multiline: true },
  affiliation: { key: "affiliation" },
  courseNumber: { key: "courseNumber" },
  courseName: { key: "courseName" },
  instructor: { key: "instructor" },
  dueDate: { key: "dueDate" },
  runningHead: { key: "runningHead" },
  authorNote: { key: "authorNote", multiline: true },
};

const STYLE_OPTIONS: TitlePageStyle[] = [
  "apa-student",
  "apa-professional",
  "mla",
];

const STYLE_FIELDS: Record<TitlePageStyle, FieldKey[]> = {
  "apa-student": [
    "title",
    "authors",
    "affiliation",
    "courseNumber",
    "courseName",
    "instructor",
    "dueDate",
  ],
  "apa-professional": [
    "title",
    "authors",
    "affiliation",
    "runningHead",
    "authorNote",
  ],
  mla: [
    "authors",
    "instructor",
    "courseNumber",
    "courseName",
    "dueDate",
    "title",
  ],
};

const EMPTY_INPUT: TitlePageInput = {
  title: "",
  authors: "",
  affiliation: "",
  courseNumber: "",
  courseName: "",
  instructor: "",
  dueDate: "",
  runningHead: "",
  authorNote: "",
};

function PreviewBlockView({ block }: { block: PreviewBlock }) {
  const align = block.align === "center" ? "text-center" : "text-left";
  return (
    <p className={`${align} leading-loose`}>
      {block.lines.map((line, i) => (
        <span key={`${line.text}-${i}`}>
          {i > 0 && <br />}
          {line.bold ? <strong>{line.text}</strong> : line.text}
        </span>
      ))}
    </p>
  );
}

function openPrintWindow(html: string, strings: Strings): void {
  const win = window.open("", "_blank");
  if (!win) {
    toast.error(strings.popupBlocked);
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

/** English strings. A translated content file overrides these through `ui`. */
export const TITLE_PAGE_GENERATOR_STRINGS = {
  style: "Style",
  preview: "Preview",
  fillInTheFieldsTo: "Fill in the fields to see your title page.",
  copied: "Copied!",
  copyAsWordReadyHtml: "Copy as Word-ready HTML",
  print: "Print",
  popupBlocked: "Couldn't open the print view. Please allow pop-ups.",
  styleLabels: {
    "apa-student": "APA 7 (Student)",
    "apa-professional": "APA 7 (Professional)",
    mla: "MLA 9",
  },
  fieldLabels: {
    title: "Paper title",
    authors: "Author name(s)",
    affiliation: "Affiliation / institution",
    courseNumber: "Course number",
    courseName: "Course name",
    instructor: "Instructor",
    dueDate: "Due date",
    runningHead: "Running head (max 50 characters)",
    authorNote: "Author note",
  },
  fieldPlaceholders: {
    title: "The Effects of Sleep on Memory",
    authors: "Jane Q. Student (one per line for multiple)",
    affiliation: "Department of Psychology, State University",
    courseNumber: "PSY 101",
    courseName: "Introduction to Psychology",
    instructor: "Dr. Alan Grant",
    dueDate: "March 14, 2026",
    runningHead: "SLEEP AND MEMORY",
    authorNote: "This paper was prepared for…",
  },
} as const;

type Strings = typeof TITLE_PAGE_GENERATOR_STRINGS;

export default function TitlePageGeneratorWidget() {
  const [style, setStyle] = useState<TitlePageStyle>("apa-student");
  const [input, setInput] = useState<TitlePageInput>(EMPTY_INPUT);

  const visibleFields = STYLE_FIELDS[style];
  const preview = buildTitlePage(style, input);
  const hasContent = preview.blocks.length > 0;

  function update(key: FieldKey, value: string) {
    setInput((prev) => ({ ...prev, [key]: value }));
  }

  function handlePrint() {
    openPrintWindow(buildTitlePageWordHtml(style, input), strings);
  }

  const strings = useToolUi(TITLE_PAGE_GENERATOR_STRINGS);

  return (
    <>
      {/* Style selector */}
      <section className="space-y-2">
        <p className="font-medium text-foreground text-sm">{strings.style}</p>
        <div className="flex flex-wrap gap-3">
          {STYLE_OPTIONS.map((opt) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={opt}
            >
              <input
                checked={style === opt}
                className="accent-primary"
                name="style"
                onChange={() => {
                  setStyle(opt);
                }}
                type="radio"
                value={opt}
              />
              {strings.styleLabels[opt]}
            </label>
          ))}
        </div>
      </section>
      <div className="grid gap-8 md:grid-cols-2">
        {/* Form */}
        <section className="space-y-4">
          {visibleFields.map((key) => {
            const field = FIELDS[key];
            return (
              <div className="space-y-1.5" key={key}>
                <label
                  className="font-medium text-foreground text-sm"
                  htmlFor={`field-${key}`}
                >
                  {strings.fieldLabels[key]}
                </label>
                {field.multiline ? (
                  <textarea
                    className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    id={`field-${key}`}
                    onChange={(e) => update(key, e.target.value)}
                    placeholder={strings.fieldPlaceholders[key]}
                    value={input[key]}
                  />
                ) : (
                  <input
                    className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    id={`field-${key}`}
                    onChange={(e) => update(key, e.target.value)}
                    placeholder={strings.fieldPlaceholders[key]}
                    type="text"
                    value={input[key]}
                  />
                )}
              </div>
            );
          })}
        </section>

        {/* Preview */}
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.preview}
          </p>
          <div className="rounded-lg border border-border bg-white p-6 font-serif text-[13px] text-black shadow-sm">
            <div className="flex justify-between text-black/70">
              <span>{preview.header.left ?? " "}</span>
              <span>{preview.header.right}</span>
            </div>
            {hasContent ? (
              <div
                className={`mt-3 space-y-3 ${preview.layout === "apa" ? "pt-16 text-center" : ""}`}
              >
                {preview.blocks.map((block, i) => (
                  <PreviewBlockView block={block} key={`${block.align}-${i}`} />
                ))}
              </div>
            ) : (
              <p className="mt-6 text-black/40">{strings.fillInTheFieldsTo}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              disabled={!hasContent}
              html={buildTitlePageFragment(style, input)}
              label={strings.copyAsWordReadyHtml}
              text={buildTitlePagePlainText(style, input)}
            />
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
              disabled={!hasContent}
              onClick={handlePrint}
              type="button"
            >
              {strings.print}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
